/**
 * QuotedReply — the block at the top of a bubble that says WHICH earlier
 * message this one answers (reply to a specific message, Miles 2026-09-15).
 *
 * Who wrote the target, and its opening words, under an accent bar — the
 * shape every messaging surface trains the eye on. The whole block is a
 * button: pressing it scrolls the transcript to the original and glows it,
 * through the same effect a thread-search hit uses (the host owns that; this
 * component only reports the press). Adapts to the ground it sits on — the
 * viewer's own gold bubble or a received canvas bubble.
 *
 * Copy here is UI chrome (D9 — state, never content): the two words that
 * frame someone else's excerpt, and the accessible name of the jump. The
 * excerpt itself is a verbatim excerpt of what somebody already wrote.
 *
 * Gold is a FILL, never text on a light ground (theme.ts, 2026-09-12): the
 * accent bar is a border colour, the label on canvas uses --brand-gold-text.
 */
import React from 'react';
import styled from 'styled-components';
import { splitMentionParts } from '../../services/threadMentions/mentionChip';
import type { MentionChipPerson } from '../../services/threadMentions/mentionChip';

const Block = styled.button<{ $onGold: boolean }>`
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin: 0 0 8px;
  padding: 6px 10px 6px 12px;
  text-align: left;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.8rem;
  line-height: 1.35;
  border: none;
  border-left: 3px solid ${(p) => (p.$onGold ? 'rgba(43, 43, 43, 0.55)' : 'var(--brand-gold)')};
  border-radius: 10px;
  background: ${(p) => (p.$onGold ? 'rgba(255, 255, 255, 0.34)' : 'rgba(198, 167, 94, 0.10)')};
  /* ONE COLOUR ON THE GOLD (Miles, 2026-09-22). The quote used white on a
     white-18% wash over gold — about 2.3:1, the same defect as the bubble
     body it sits in. Charcoal on a slightly stronger wash reads at a
     comfortable multiple of AA and matches the message around it, so a quoted
     mention, the quote's words and the sentence beneath are now one colour. */
  color: ${(p) => (p.$onGold ? 'var(--brand-charcoal)' : 'var(--brand-text)')};
  cursor: pointer;
  /* The bubble is white-space: pre-wrap; a quote is prose. */
  white-space: normal;
  transition: background 0.15s ease;

  &:hover {
    background: ${(p) => (p.$onGold ? 'rgba(255, 255, 255, 0.46)' : 'rgba(198, 167, 94, 0.16)')};
  }

  &:focus-visible {
    outline: 2px solid ${(p) => (p.$onGold ? 'rgba(43, 43, 43, 0.75)' : 'var(--brand-gold)')};
    outline-offset: 1px;
  }
`;

const Who = styled.span<{ $onGold: boolean }>`
  display: block;
  font-weight: 600;
  font-size: 0.72rem;
  letter-spacing: 0.01em;
  color: ${(p) => (p.$onGold ? 'var(--brand-charcoal)' : 'var(--brand-gold-text)')};
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Excerpt = styled.span<{ $onGold: boolean }>`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: ${(p) => (p.$onGold ? 'rgba(43, 43, 43, 0.82)' : 'var(--brand-text-secondary)')};
  word-break: break-word;
  overflow-wrap: anywhere;
`;

/**
 * A mention inside the quote (Miles, 2026-09-22 — the mentions in his own
 * screenshot were all in these blocks, reading as grey `@handle`).
 *
 * The person's NAME on the same solid ground the bubble's chip uses, so a
 * quote cannot be the one place a confirmed mention looks like a miss. Two
 * differences from the chip, both because a quote is a PREVIEW of somebody
 * else's message and is deliberately recessive: no glyph circle — at 0.8rem
 * under a two-line clamp it is noise, not information — and the pill sits at
 * the quote's own size.
 *
 * `display: inline` for exactly the reason the chip uses it: an inline box
 * takes its baseline from the font, so it cannot ride high in a line of text
 * the way an inline-flex box does.
 */
const QuotedMention = styled.span<{ $onGold: boolean }>`
  display: inline;
  padding: 0.05em 0.42em;
  border-radius: 999px;
  font-weight: 600;
  white-space: nowrap;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
  background: ${(p) => (p.$onGold ? 'rgba(255, 255, 255, 0.92)' : 'var(--brand-gold)')};
  color: var(--brand-charcoal);
`;

// copy: quoted_reply_chrome
export const QUOTED_REPLY_COPY = {
  en: { replyingTo: 'replying to', jump: 'jump to the original message', earlier: 'earlier message' },
  es: { replyingTo: 'respondiendo a', jump: 'ir al mensaje original', earlier: 'mensaje anterior' },
} as const;

export interface QuotedReplyProps {
  /** Who wrote the message being answered — resolved by the host's label chain. */
  label: string;
  /** The target's opening words; absent for an attachment-only target. */
  excerpt?: string;
  /** Rendered inside the viewer's own gold bubble. */
  onGold?: boolean;
  /** Scroll to the original (the host's search-hit effect). */
  onJump?: () => void;
  /**
   * The mention chip's own resolver — exactly the people a send reaches, or
   * null for a handle that reaches nobody. Absent ⇒ the excerpt renders as
   * plain text, exactly as it did before (2026-09-22).
   */
  resolveMention?: ((handle: string) => MentionChipPerson | null) | null;
  lang?: 'en' | 'es';
}

export function QuotedReply({ label, excerpt, onGold = false, onJump, resolveMention, lang = 'en' }: QuotedReplyProps): React.ReactElement {
  const copy = QUOTED_REPLY_COPY[lang] ?? QUOTED_REPLY_COPY.en;
  const who = (label || '').trim();
  const words = (excerpt || '').trim();
  /**
   * React NODES, never a dangerouslySetInnerHTML sink. The excerpt is a
   * verbatim copy of what somebody wrote, and this component has never had an
   * html sink; adding one to decorate a mention would put an escaping
   * obligation on a component that currently cannot have one. React escapes
   * these for free.
   */
  const excerptNodes = React.useMemo(() => {
    if (!words || !resolveMention) return null;
    const parts = splitMentionParts(words, resolveMention);
    if (!parts.some((part) => part.kind === 'mention')) return null;
    return parts.map((part, i) => (part.kind === 'text'
      ? <React.Fragment key={i}>{part.text}</React.Fragment>
      : (
        <QuotedMention key={i} $onGold={onGold} data-quoted-reply-mention={part.handle}>
          {part.person.name}
        </QuotedMention>
      )));
  }, [words, resolveMention, onGold]);
  return (
    <Block
      type="button"
      $onGold={onGold}
      data-quoted-reply=""
      aria-label={`${copy.jump} — ${copy.replyingTo} ${who}`}
      title={copy.jump}
      onClick={(e) => {
        e.stopPropagation();
        onJump?.();
      }}
    >
      <Who $onGold={onGold} data-quoted-reply-who="">{who}</Who>
      <Excerpt $onGold={onGold} data-quoted-reply-excerpt="">{excerptNodes ?? (words || copy.earlier)}</Excerpt>
    </Block>
  );
}

export default QuotedReply;
